/**
 * Executive Nutrition Dashboard — Phase 2.1B
 *
 * Bound to: GET /api/nutrition-analytics/organization
 *
 * Org-wide overview:
 *   1. Org KPI strip
 *   2. Team comparison table
 *   3. Org Risk Overview (top 50 risk athletes, cross-team)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Apple, Crown, Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const fmt = (v, dp = 1, suffix = '') =>
  v === null || v === undefined ? '—' : `${Number(v).toFixed(dp)}${suffix}`;
const fmtInt = (v, suffix = '') =>
  v === null || v === undefined ? '—' : `${Math.round(v)}${suffix}`;

const StatCard = ({ label, value, testid }) => (
  <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</div>
    <div className="text-2xl font-bold text-cyan-300">{value}</div>
  </Card>
);

const ExecutiveNutritionDashboardPage = () => {
  const { api } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get('/nutrition-analytics/organization');
        setData(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api]);

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center text-zinc-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        {language === 'IT' ? 'Caricamento…' : 'Loading…'}
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-12 text-center text-zinc-400" data-testid="exec-nutrition-error">
        {error}
      </div>
    );
  }

  const avg = data.org_averages || {};
  const comp = data.team_comparison || [];
  const risks = data.risk_overview || [];

  return (
    <div className="space-y-6 p-6" data-testid="executive-nutrition-dashboard">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/staff/nutrition')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            {language === 'IT' ? 'Indietro' : 'Back'}
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Crown className="w-6 h-6 text-amber-400" />
              <Apple className="w-6 h-6 text-emerald-400" />
              <h1 className="text-3xl font-bold text-zinc-100">
                {language === 'IT' ? 'Suite Executive — Nutrizione' : 'Executive Nutrition Suite'}
              </h1>
            </div>
            <p className="text-sm text-zinc-400">
              {data.athletes_total} {language === 'IT' ? 'atleti' : 'athletes'} ·{' '}
              {data.team_count} {language === 'IT' ? 'squadre' : 'teams'} ·{' '}
              {data.athletes_with_anthro} {language === 'IT' ? 'con misurazioni' : 'with measurements'}
            </p>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard testid="org-avg-weight" label={language === 'IT' ? 'Peso medio org' : 'Org avg weight'} value={fmt(avg.weight_kg, 1, ' kg')} />
        <StatCard testid="org-avg-bf" label="BF % medio" value={fmt(avg.body_fat_pct, 1, ' %')} />
        <StatCard testid="org-avg-lean" label={language === 'IT' ? 'Magra media' : 'Avg lean'} value={fmt(avg.lean_mass_kg, 1, ' kg')} />
        <StatCard testid="org-avg-bmr" label="BMR medio" value={fmtInt(avg.bmr_kcal, ' kcal')} />
        <StatCard testid="org-avg-tdee" label="TDEE medio" value={fmtInt(avg.tdee_kcal, ' kcal')} />
      </div>

      <Card className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid="team-comparison-table">
        <div className="p-4 border-b border-zinc-800">
          <h2 className="text-lg font-semibold text-zinc-100">
            {language === 'IT' ? 'Confronto Squadre' : 'Team Comparison'}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-950 text-zinc-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">{language === 'IT' ? 'Squadra' : 'Team'}</th>
                <th className="px-4 py-3 text-right">{language === 'IT' ? 'Atleti' : 'Athletes'}</th>
                <th className="px-4 py-3 text-right">{language === 'IT' ? 'Con misurazioni' : 'With measurements'}</th>
                <th className="px-4 py-3 text-right">{language === 'IT' ? 'Peso medio' : 'Avg weight'}</th>
                <th className="px-4 py-3 text-right">BF % medio</th>
                <th className="px-4 py-3 text-right">TDEE medio</th>
                <th className="px-4 py-3 text-center">{language === 'IT' ? 'A rischio' : 'At risk'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {comp.map((t) => (
                <tr
                  key={t.team_id}
                  className="hover:bg-zinc-800/60 cursor-pointer"
                  onClick={() => navigate(`/staff/nutrition/teams/${t.team_id}`)}
                  data-testid={`team-comp-${t.team_id}`}
                >
                  <td className="px-4 py-3 text-zinc-100">
                    {t.code ? `${t.code} — ${t.team_name}` : t.team_name}
                  </td>
                  <td className="px-4 py-3 text-right text-zinc-200">{t.athletes_total}</td>
                  <td className="px-4 py-3 text-right text-zinc-200">{t.athletes_with_anthro}</td>
                  <td className="px-4 py-3 text-right text-zinc-200">{fmt(t.avg_weight_kg)}</td>
                  <td className="px-4 py-3 text-right text-zinc-200">{fmt(t.avg_body_fat_pct)}</td>
                  <td className="px-4 py-3 text-right text-zinc-200">{fmtInt(t.avg_tdee_kcal)}</td>
                  <td className="px-4 py-3 text-center">
                    {t.risk_count > 0 ? (
                      <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40">{t.risk_count}</Badge>
                    ) : (
                      <span className="text-zinc-600">0</span>
                    )}
                  </td>
                </tr>
              ))}
              {comp.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">—</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="org-risk-overview">
        <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2 mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          {language === 'IT' ? 'Panoramica rischio organizzazione' : 'Org Risk Overview'}
          <Badge variant="outline" className="border-amber-500/50 text-amber-300 ml-2">
            {risks.length}
          </Badge>
        </h2>
        {risks.length === 0 ? (
          <p className="text-sm text-zinc-500">{language === 'IT' ? 'Nessun atleta a rischio.' : 'No flagged athletes.'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-zinc-400 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">{language === 'IT' ? 'Squadra' : 'Team'}</th>
                  <th className="px-3 py-2 text-left">{language === 'IT' ? 'Atleta' : 'Athlete'}</th>
                  <th className="px-3 py-2 text-left">{language === 'IT' ? 'Bandiere' : 'Flags'}</th>
                  <th className="px-3 py-2 text-center">{language === 'IT' ? 'Severità' : 'Severity'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {risks.map((r) => (
                  <tr
                    key={`${r.team_id}-${r.athlete_id}`}
                    className="hover:bg-zinc-800/60 cursor-pointer"
                    onClick={() => navigate(`/staff/nutrition/athletes/${r.athlete_id}`)}
                  >
                    <td className="px-3 py-2 text-zinc-400">{r.team_name}</td>
                    <td className="px-3 py-2 text-zinc-100">{r.first_name} {r.last_name}</td>
                    <td className="px-3 py-2 text-zinc-300">
                      <div className="flex flex-wrap gap-1.5">
                        {r.flags.map((f, i) => (
                          <Badge key={i} variant="outline" className={
                            f.rule === 'RAPID_WEIGHT_LOSS' ? 'border-red-500/50 text-red-300 bg-red-500/10' :
                            f.rule === 'RAPID_WEIGHT_GAIN' ? 'border-amber-500/50 text-amber-300 bg-amber-500/10' :
                            f.rule === 'HIGH_BF_PCT' ? 'border-orange-500/50 text-orange-300 bg-orange-500/10' :
                            'border-purple-500/50 text-purple-300 bg-purple-500/10'
                          }>
                            {f.rule.replace(/_/g, ' ')} ({f.value_pct?.toFixed?.(1)}%)
                          </Badge>
                        ))}
                      </div>
                      {r.flags[0]?.source_label && (
                        <div className="text-[10px] text-zinc-500 mt-1">
                          {language === 'IT' ? 'Soglia' : 'Threshold'}: {r.flags[0].source_label}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40">{r.severity_score}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ExecutiveNutritionDashboardPage;
